import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ECharts, EChartsOption } from "echarts/types/dist/echarts";
import { NzMessageService } from "ng-zorro-antd/message";
import { ProjectService } from "../../../shared/services/project.service";
import { ActivatedRoute, Router } from "@angular/router";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { Project } from "../../../models/project";
import * as _ from "lodash";
import { parameter_view_fields } from "../parameter-overview/fields";
import { FormBuilder, FormControl } from "@angular/forms";
import { take } from "rxjs/operators";

interface HeatmapConfig {
  x: FormControl;
  y: FormControl;
  colorRange: number[];
  treeviewState?: any[]; // this one is to store the state of the tree. Might be redundant tho.
}

interface GeoShotType {
  shot_id: string;
  x: string;
  y: string;
  z: string;
  [metric_type: string]: string | number;
  // trace_fit: number;
  // fnlc: number;
  // dc_raw: number;
}

interface JobItem {
  id: string;
  job__id: string;
  job__job_name: string;
  job__job_basepath: string;
}

interface GroupItem {
  id: string;
  name: string;
  project_name: string;
  created_by_name: string;
  comment: string;
  jobs: JobItem[];
}

@Component({
    selector: "app-trace-fits-plotting",
    templateUrl: "./trace-fits-plotting.component.html",
    styleUrls: ["./trace-fits-plotting.component.less"],
    standalone: false
})
export class TraceFitsPlottingComponent implements OnInit {
  @ViewChild("graph", { static: true }) graph: ElementRef;
  chartInstance: ECharts;

  inputType: "jobs" | "jsonLoad" = "jobs";

  ylim: any[] = [0, 100];
  xlim: any[] = [0, 0];
  xMin: number = 0;
  xMax: number = 0;
  yMin: number = 0;
  yMax: number = 100;
  spatial_iteration: number = 1;

  project_id: string = "";
  project_name: string = "";
  loadingChart: boolean = false;
  error: string = null;
  searchValue: string = "";
  options: EChartsOption | null;
  frequencyData: {
    job_id: string;
    job_name: string;
    frequency: string;
    iters: number;
    iterations: string;
    iteration: number;
    iteration_frequency: number;
  }[] = [];
  data: {
    metrics: {
      [metric_types: string]: string;
    };
    tracedata: {
      [job_name: string]: {
        job_data: { [key: string]: any };
        iterations_data: {
          [metric_key: string]: { [iteration: string]: number };
        };
        spatial_data: {
          [iteration: string]: { [shot_id: string]: GeoShotType };
        };
        // iterations_trace: object;
        // iterations_dc_raw: object;
        // iterations_fnlc: object;
        // frequency: object;
        // shot_with_geo: {
        //   [iteration: string]: { [shot_id: string]: GeoShotType };
        // };
      };
    };
  } = {
    metrics: {},
    tracedata: {},
  };

  paramViewFields: object = parameter_view_fields;
  heatmapParams: string[] = ["awi_scale", "awi_top"];
  heatX: string = "awi_scale";
  heatY: string = "awi_top";
  heatmapConfigs: HeatmapConfig[] = [];
  heatmapAxisSelectionModal: boolean = false;
  activeConfig: number = 1;
  treeviewInitValue: any[] = [];
  heatmapOptions: EChartsOption[];

  tracetype: string = "traces_fit";
  linePlotXConfig: "spatial" | "iteration" = "spatial";
  iteration_type: "global" | "per-job" = "global";
  spatial_plotType: "scatter" | "line" = "line";
  totalIterations: any;
  spatial_totalIterations: any;

  jobsSelected: any[] = [];
  jobsSelectedIds: string[] = [];
  jobsWithoutData = new Set<string>();
  allJobs: any[] = [];
  is2d: boolean;
  tolerance: number = 100;
  swapAxis: boolean = false;
  areFrequenciesComparable: boolean = true;
  imageName: string = "plot";
  loadingGroups: boolean;
  jobGroups: GroupItem[] = [];
  selectedGroups: GroupItem[] = [];
  limitsInputModeSlider: boolean = true;

  // JSON plotting mode
  file_path: string = "";
  json_parsedData: any[] = [];
  json_metrics: any = {};
  json_fit_type: string = "";
  json_swapAxis: boolean = false;
  json_xField: string = 'inline';
  json_yField: string = '';
  json_chartType: 'top_down' | 'inline_crossline' = 'inline_crossline';
  json_plotType: 'scatter' | 'line' = 'scatter';
  json_fitValues: number[] = [];
  json_minFit: number = 0;
  json_maxFit: number = 0;
  json_fitRange: number = 1;
  json_tracedata: any = {};
  sciFormatter = (value: number) => (value == null ? '' : value === 0 ? '0' : value.toExponential(2));
  sciParser = (value: string) => parseFloat(value);

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _projectService: ProjectService,
    private _cachedProjectService: CachedProjectService,
    private _message: NzMessageService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this._cachedProjectService.currentProject.subscribe((project: Project) => {
      if (project != null) {
        this.project_id = project.id;
        this.project_name = project.name;

        this._cachedProjectService
          ._getProjectJobs(this.project_id, {
            fields: ["id", "job_name", "iterations", "project__id"],
          })
          .toPromise()
          .then((res) => {
            this.allJobs = res["data"].filter(
              (j) => !j.job_name.includes("QC")
            );
            this.getJobGroups();

            // if queryParams have jobs to actually plot
            if (this._route.snapshot.queryParams.jobs) {
              let jobIds = this._route.snapshot.queryParams.jobs;
              if (!_.isArray(jobIds)) {
                jobIds = [jobIds];
              }

              let qc_ids = [];
              jobIds?.forEach((id: string) => {
                let selectedJob = this.allJobs.find((j) => j.id == id);
                if (selectedJob) {
                  this.jobsSelectedIds.push(selectedJob.id);
                  this.jobsSelected.push({
                    ...selectedJob,
                    currentIteration: 1,
                  });
                } else {
                  qc_ids.push(id);
                }
              });
              this.getPlottingDataForParamJobs();
            }
          });
      }
    });
  }

  getIterationFrequency(
    iters: number,
    iterations: string,
    frequency: string,
    iteration: number
  ) {
    let freq = JSON.parse(frequency);
    // Sometimes the iterations are like [-, 3, 3, -, 3, 3]. The dashes represent places where essentialy the iters value was used. But you don't replace it, the JSON parse seems it as a minus sign
    // and gives an error cause ofcourse no number after the minus sign. So we replace it with the iters value
    iterations = iterations?.replace(/-/g, String(iters));
    let iterationsArray = JSON.parse(iterations || "[]");

    let cumulativeSum = 0;
    let index = -1;

    /**
     * Case 1: no iterations? so just use iters
     * Case 2: only 1 iteration? use that same way as the iters
     * Case 3: multiple iterations? use the iterations array
     */
    if (!iterationsArray || iterationsArray.length == 0) {
      if (iteration === 0) {
        return freq[0];
      }
      if (iters && freq) {
        return freq[Math.floor((iteration - 1) / iters)] || null;
      }
    } else if (iterationsArray.length == 1) {
      if (iteration === 0) {
        return freq[0];
      }
      if (iters && freq) {
        return freq[Math.floor((iteration - 1) / iterationsArray[0])] || null;
      }
    } else {
      for (let idx = 0; idx < iterationsArray.length; idx++) {
        cumulativeSum += Number(iterationsArray[idx]);
        if (cumulativeSum >= iteration) {
          index = idx;
          break;
        }
      }
      if (index == -1) {
        return null;
      } else {
        return freq[index] || null;
      }
    }
  }

  getFrequencyData() {
    let project_id = this.jobsSelected[0]["project__id"];
    this._projectService
      .getParameterTable(project_id, false, this.jobsSelectedIds)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.frequencyData = res["results"].map((j) => {
            let job_current_iteration = this.jobsSelected.find(
              (js) => js.id == j["job_id"]
            )["currentIteration"];
            return {
              job_id: j["job_id"],
              job_name: j["job_name"],
              frequency: j["frequency"],
              iterations: j["iterations"],
              iteration: job_current_iteration,
              iteration_frequency: this.getIterationFrequency(
                j["iters"],
                j["iterations"],
                j["frequency"],
                job_current_iteration
              ),
              iters: Number(j["iters"]),
            };
          });
        },
      });
  }

  refreshFrequencyData() {
    this.frequencyData = this.frequencyData.map((d) => {
      var job_current_iteration;

      if (this.iteration_type == "global") {
        job_current_iteration = this.spatial_iteration;
      } else {
        job_current_iteration = this.jobsSelected.find(
          (js) => js.id == d["job_id"]
        )["currentIteration"];
      }

      return {
        ...d,
        iteration: job_current_iteration,
        iteration_frequency: this.getIterationFrequency(
          d.iters,
          d.iterations,
          d.frequency,
          job_current_iteration
        ),
      };
    });
  }

  getPlottingDataForParamJobs() {
    this.loadingChart = true;
    let jobIds = this._route.snapshot.queryParams.jobs;
    if (!_.isArray(jobIds)) {
      jobIds = [jobIds];
    }
    this._cachedProjectService.currentProject.subscribe((project: Project) => {
      if (project != null) {
        this.project_id = project.id;
        this.project_name = project.name;
        this._projectService
          .getTraceFitPlottingData(this.project_id, {
            job_ids: jobIds,
            parameters: [
              "awi_scale",
              "awi_normfilt",
              "job__iterations",
              "job__comments",
            ],
          })
          .toPromise()
          .then((res) => {
            this.data = res;
            this.getFrequencyData();
            this.checkFrequencyDifference();

            // set the global iteration to min of iterations completed for all the jobs
            this.spatial_iteration = _.min(
              this.jobsSelected
                .map((job) => job.iterations || null)
                .filter((i) => !!i)
            );

            this.plotLineChart();
          })
          .catch((err) => {
            console.error(err);
            this._message.error(err.message);
          })
          .finally(() => (this.loadingChart = false));
      }
    });
  }

  /**
   * update via updating the URL. Might seem unintuitive but actually better cause allows sharing and not really that tedious
   */
  plotSelectedJobs() {
    if (this.jobsSelectedIds?.length > 0) {
      this._router
        .navigate(["."], {
          relativeTo: this._route,
          queryParams: { jobs: this.jobsSelectedIds },
          replaceUrl: true,
        })
        .then((_) => this.getPlottingDataForParamJobs());
    }
  }

  /**
   * Updates the selected jobs array based on provided job IDs
   * Filters jobs from allJobs that match the provided IDs and maps them with an initial iteration of 1
   *
   * @param data Array of job IDs to select
   */
  updateJobsSelected(data: string[]) {
    this.jobsSelected = this.allJobs
      .filter((j) => data.includes(j.id))
      .map((j) => ({ ...j, currentIteration: 1 }));
  }

  chartInit(event) {
    this.chartInstance = event;
  }

  onYlimChanged(e) {
    this.chartInstance.setOption({
      yAxis: {
        min: e[0],
        max: e[1],
        type: "value",
      },
    });
  }
  onXlimChanged(e) {
    this.chartInstance.setOption({
      xAxis: {
        min: e[0] - 1,
        max: e[1] - 1,
        type: "category",
        boundaryGap: false,
      },
    });
  }

  changePlotType() {
    // reinitialize axis to
    this.xlim = [null, null];
    this.ylim = [null, null];
    this.plotLineChart();
  }

  plotLineChart() {
    this.imageName = "TFP-" + this.project_name.substring(0, 10);
    if (this.linePlotXConfig == "iteration") {
      this.imageName += "-" + this.tracetype;
      this.imageName += "-iteration-plot-";
      this.imageName += this.jobsSelected
        .map((j) => j.job_name.substring(0, 7))
        .join("-");
      this.makeIterationLineChart();
    } else if (this.linePlotXConfig == "spatial") {
      this.refreshFrequencyData();

      this.imageName += "-" + this.tracetype;
      this.imageName += "-spatial-plot";
      this.imageName += "-" + this.spatial_plotType + "-";
      this.imageName += this.jobsSelected
        .map((j) => j.job_name.substring(0, 7))
        .join("-");
      this.makeSpatialLineChart();
    }
  }

  makeSpatialLineChart() {
    let options = this.chartInstance?.getOption();
    let savedLegendState = options?.legend[0].selected;

    this.chartInstance?.clear();

    let job_datas = Object.keys(this.data.tracedata).map(
      (k) => this.data.tracedata[k].job_data
    );

    // avg tracefit over a position across iterations
    let shotSet = new Set<number>();
    let averagingDict: Record<string, Record<string, any>> = {};
    let minX: number = null;
    let minY: number = null;
    let maxY: number = null;

    // the results contains job info. So the dx and nx1 can be fetched from here
    this.xMax = _.max(
      Object.values(this.data.tracedata).map((j) => {
        if (j.job_data.job__x2_is_inline) {
          if (this.swapAxis) {
            return Number(j.job_data.nx1) * Number(j.job_data.dx);
          } else {
            return Number(j.job_data.nx2) * Number(j.job_data.dx);
          }
        } else {
          if (this.swapAxis) {
            return Number(j.job_data.nx2) * Number(j.job_data.dx);
          } else {
            return Number(j.job_data.nx1) * Number(j.job_data.dx);
          }
        }
      })
    );

    if ((this.xlim[1] && this.xlim[1] > this.xMax) || !this.xlim[1]) {
      this.xlim = [0, this.xMax];
    }

    if (job_datas.length > 0) {
      this.is2d = job_datas[0].job__model_grid__is_2d;
    }

    for (let job of Object.keys(this.data.tracedata)) {
      const job_info = this.data.tracedata[job].job_data;

      if (!(job in averagingDict)) {
        averagingDict[job] = {};
      }
      const shots_with_geo_by_iteration = this.data.tracedata[job].spatial_data;

      let totalIterations = job_info["job__iterations"];

      if (
        !this.spatial_totalIterations ||
        this.spatial_totalIterations < totalIterations
      ) {
        this.spatial_totalIterations = totalIterations;
      }

      var iteration: number;
      if (this.iteration_type == "global") {
        iteration = this.spatial_iteration;
      } else {
        iteration = this.jobsSelected.find((j) => j.id == job).currentIteration;
      }

      let iteration_shots = shots_with_geo_by_iteration[`${iteration}`];

      if (iteration_shots) {
        for (let shot of Object.values(iteration_shots)) {
          if (!shot[this.tracetype]) continue;

          shotSet.add(Number(shot.shot_id)); //it will only add the unique ones since its a set
          if (`${shot.shot_id}` in averagingDict[job]) {
            let value = null;
            value = shot[this.tracetype];
            // if (this.tracetype == "trace_fit") value = shot.trace_fit;
            // else if (this.tracetype == "dc_raw") value = shot.dc_raw;
            // else value = shot.fnlc;

            averagingDict[job][`${shot.shot_id}`]["fits"].push(value);
            if (!maxY || maxY < Number(value)) maxY = Number(value.toFixed(3));
            if (!minY || minY > Number(value)) minY = Number(value.toFixed(3));
          } else {
            let value = null;
            averagingDict[job][`${shot.shot_id}`] = {};
            averagingDict[job][`${shot.shot_id}`]["fits"] = [shot.trace_fit];

            value = shot[this.tracetype];
            // if (this.tracetype == "trace_fit") value = shot.trace_fit;
            // else if (this.tracetype == "dc_raw") value = shot.dc_raw;
            // else value = shot.fnlc;

            if (job_info["job__x2_is_inline"]) {
              // x = inline, y = crossline (normally)
              // if x2_is_inline, x and y are swapped
              // now we wanna do one more check, if the user swappedAxis or not.
              // doing it like this (nested ifs) may not be the best, but it is simple solution. No point overcomplicating when it can be done in just 1 if statement
              if (this.swapAxis) {
                averagingDict[job][`${shot.shot_id}`]["x"] = shot.x;
              } else {
                averagingDict[job][`${shot.shot_id}`]["x"] = shot.y;
              }
            } else {
              if (this.swapAxis) {
                averagingDict[job][`${shot.shot_id}`]["x"] = shot.y;
              } else {
                averagingDict[job][`${shot.shot_id}`]["x"] = shot.x;
              }
            }

            averagingDict[job][`${shot.shot_id}`]["fits"] = [value];

            if ((!maxY || maxY < Number(value)) && value)
              maxY = Number(value.toFixed(3));
            if ((!minY || minY > Number(value)) && value)
              minY = Number(value.toFixed(3));

            if (!minX || Number(minX) > Number(shot.x)) {
              minX = Number(shot.x);
            }
          }
        }
      }
    }

    this.yMax = !maxY || maxY < 100 ? 100 : maxY;
    this.yMin = !minY || minY > 0 ? 0 : minY;
    // clone helps update state
    // assign values to yLim only if any of the values are out of the range sort of. Basically preserve the limits
    this.ylim[0] =
      !this.ylim[0] || this.ylim[0] < this.yMin ? this.yMin : this.ylim[0];
    this.ylim[1] =
      !this.ylim[1] || this.ylim[1] > this.yMax ? this.yMax : this.ylim[1];

    this.ylim = _.cloneDeep(this.ylim);

    for (let jobInfo of Object.values(averagingDict)) {
      for (let shotInfo of Object.values(jobInfo)) {
        shotInfo["fit"] = _.mean(shotInfo["fits"]);
      }
    }

    this.jobsWithoutData = new Set(
      Object.entries(averagingDict)
        .filter(([, shots]) => Object.keys(shots).length === 0)
        .map(([job]) => job)
    );

    // the averagingDict has the jobs as keys, and their shots with the tracefits and their x positions
    const xaxisData = Array.from(
      { length: Math.floor(this.xMax) },
      (_, i) => i
    );

    const seriesData = [];
    for (let job of Object.keys(averagingDict)) {
      const jobDict = averagingDict[job];
      const linedata = [];

      // the x2_is_inline, will switch the x and z values of the data. So here, if the job has x2_is_inlin true, use z.
      Object.values(jobDict).forEach((shot: object) => {
        linedata[Math.floor(Number(shot["x"]))] =
          Number(shot["fit"])?.toFixed(2) || null;
      });
      const newLindedata = this.resolve3DSlices(linedata);

      seriesData.push({
        connectNulls: true,
        data: newLindedata,
        name: this.data.tracedata[job].job_data["job__job_name"],
        type: this.spatial_plotType,
        symbolSize: 5,

        animation: false,
        tooltip: { show: true },
      });
    }

    // its just to make a reference to this.data so i can access it. Otherwise I can't use the this from inside the options obj cause different context. So just make this new variable independent of context which i can use to access it
    var dataRef = this.data;

    this.options = {
      title: {
        show: this.iteration_type == "global",
        text: `Iteration: ${this.spatial_iteration}`,
      },
      legend: {
        type: "scroll",
        padding: 20,
        data: this.makeLegendData(),
        selected: savedLegendState,

        formatter(name) {
          // format the full long name to just show the fullname
          return name.substring(0, 10);
        },
        tooltip: {
          show: true,
          formatter: function (params) {
            let j = job_datas.find((d) => d.job__job_name == params.name);
            return `<span><b>${
              params.name.length > 50
                ? params.name.replace(/(.{50})/g, "$1<br>")
                : params.name
            }</b></span><br><span style='padding-left: 5px;'>${
              j.job__comments || ""
            }</span>`;
          },
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            name: this.imageName,
          },
          restore: {},
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      tooltip: {
        trigger: "item",
        formatter: function (params) {
          if (params.name) {
            return `<span><b>${
              params.seriesName.length > 50
                ? params.seriesName.replace(/(.{50})/g, "$1<br>")
                : params.seriesName
            }</b></span><br><div style='display: flex; justify-content: space-between; gap: 20px;'><span>${
              params.marker
            }${params.name}</span><span>${params.value}</span></div>`;
          } else {
            return;
          }
        },
        axisPointer: {
          type: "cross",
          animation: false,
          label: {
            backgroundColor: "#ccc",
            borderColor: "#aaa",
            borderWidth: 1,
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            color: "#222",
          },
        },
      },
      animation: false,
      xAxis: {
        type: "category",
        name: this.swapAxis ? "Crossline" : "Inline",
        nameLocation: "middle",
        nameGap: 20,
        min: 0,
        max: this.xMax,
        nameTextStyle: {
          fontWeight: "bold",
          fontSize: 15,
        },
        boundaryGap: false,
        data: xaxisData,
      },
      yAxis: {
        type: "value",
        name: this.data.metrics[this.tracetype],
        nameLocation: "middle",
        min: this.ylim[0],
        max: this.ylim[1],
        nameTextStyle: {
          fontWeight: "bold",
          fontSize: 15,
        },
        axisLine: {
          show: true,
        },
        axisPointer: {
          show: true,
        },
        minorTick: {
          show: true,
        },
        minorSplitLine: {
          show: true,
        },
      },
      series: seriesData,
    };

    this.chartInstance?.setOption(_.cloneDeep(this.options));
    this._changeDetectorRef.detectChanges();
  }

  onInputTypeChange() {
    this.options = null;
    this.xlim = [0, 0];
    this.ylim = [0, 0];
    this.xMin = 0;
    this.xMax = 0;
    this.yMin = 0;
    this.yMax = 0;
  }

  swapSpatialAxis() {
    this.swapAxis = !this.swapAxis;
    this.plotLineChart();
  }

  resolve3DSlices(linedata: any) {
    // the case of tolerance less than 1 will default tolerance to 1
    if (this.tolerance < 1) this.tolerance = 1;

    // make buckets using the tolerance, and the total points on the line
    let limit = linedata.length;
    let buckets = Math.ceil(limit / this.tolerance);
    let breakpoints = Array.from(Array(buckets).keys()).map(
      (i) => i * this.tolerance
    );
    let indexes = breakpoints.map((i) => i + Math.floor(this.tolerance / 2));

    const newLinedata = [];

    // using the breakpoints, start getting all the tracefit values in that area and averaging
    for (let [index, bucket] of breakpoints.entries()) {
      let startIndex = bucket;
      let endIndex = startIndex + this.tolerance;
      let bucketData = linedata
        .slice(startIndex, endIndex)
        .map((v) => Number(v))
        .filter((val) => val !== null && val !== undefined)
        .map((v) => Number(v)); // endIndex not included, so no need to -1. Also filter out nulls
      let bucketValue = _.mean(bucketData); // get the avg of the bucket area. Also filter out nulls
      newLinedata[indexes[index]] = bucketValue;
    }
    return newLinedata;
  }

  makeIterationLineChart() {
    let options = this.chartInstance?.getOption();
    let savedLegendState = options?.legend[0].selected;

    let job_datas = Object.keys(this.data.tracedata).map(
      (k) => this.data.tracedata[k].job_data
    );

    this.checkFrequencyDifference();
    let yData = []; // getting this to calculate the yMin and yMax for the graph
    // very complex and dense way of basically just making an array from first to last iteration
    const preXaxisData = Array.from(
      new Set(
        Object.values(this.data.tracedata)
          .map((o) => {
            if (this.tracetype in o.iterations_data){
              yData.push(...Object.values(o.iterations_data[this.tracetype]));
                return Object.keys(o.iterations_data[this.tracetype]).map((iter) =>
                  Number(iter)
              );
            }
          })
          .flat()
      )
    ).sort(function (a, b) {
      return Number(a) - Number(b);
    });

    let local_yMin = Math.ceil(_.min(yData));
    let local_yMax = Math.ceil(_.max(yData));
    this.yMin = local_yMin < 0 ? local_yMin : 0;
    this.yMax = local_yMax > 100 ? local_yMax : 100;
    // this.ylim = [this.yMin, this.yMax];
    // assign values to yLim only if any of the values are out of the range sort of. Basically preserve the limits

    this.ylim[0] =
      !this.ylim[0] || this.ylim[0] < this.yMin ? this.yMin : this.ylim[0];
    this.ylim[1] =
      !this.ylim[1] || this.ylim[1] > this.yMax ? this.yMax : this.ylim[1];

    this.ylim = _.cloneDeep(this.ylim);

    this.xMin = 0; // xmin for this is always going to be 0 cause cannot go negative iteration
    this.xMax = _.max(preXaxisData);
    this.xlim = [this.xMin, this.xMax];

    const xaxisData = Array.from({ length: this.xMax }, (_, i) => i + 1); //=> [1, 2, 3, 4, 5, 6, 7, 8, 9...]

    // complex looking chainging but is actually very simple
    const seriesData: any[] = Object.entries(this.data.tracedata).map(
      ([job, tracefits]) => {
        const lineData = [];
        if (this.tracetype in tracefits.iterations_data){
          Object.entries(tracefits.iterations_data[this.tracetype]).forEach(
            (trf) => {
              // assign the tracefit to the index according to iter. e.g. iteration 31 will be assigned to index 30 (iteration starting from 1)
              lineData[Number(trf[0]) - 1] = Number(trf[1]).toFixed(3);
            }
          );
        }

        return {
          name: this.data.tracedata[job].job_data["job__job_name"],
          type: "line",
          connectNulls: true,
          data: lineData,
        };
      }
    );

    // its just to make a reference to this.data so i can access it. Otherwise I can't use the this from inside the options obj cause different context. So just make this new variable independent of context which i can use to access it
    const dataRef = this.data;

    this.options = {
      legend: {
        type: "scroll",
        data: this.makeLegendData(false),
        selected: savedLegendState,
        padding: 20,
        formatter(name) {
          return name.substring(0, 10);
        },
        tooltip: {
          show: true,
          formatter: function (params) {
            let j = job_datas.find((d) => d.job__job_name == params.name);
            return `<span><b>${
              params.name.length > 50
                ? params.name.replace(/(.{50})/g, "$1<br>")
                : params.name
            }</b></span><br><span style='padding-left: 5px;'>${
              j.job__comments || ""
            }</span>`;
          },
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {},
          restore: {},
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          animation: false,
          label: {
            backgroundColor: "#ccc",
            borderColor: "#aaa",
            borderWidth: 1,
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            color: "#222",
          },
        },
        formatter: function (params) {
          if (params.length == 0) return "";

          let tooltipHtml = `<b>Iteration ${params[0].axisValue} </b><br />`;
          let sortedParams = params
            .map((p) => ({
              marker: p.marker,
              seriesName: p.seriesName,
              value: p.value,
            }))
            .sort((a, b) => b.value - a.value);
          sortedParams.forEach((param) => {
            var name =
              param.seriesName?.length > 50
                ? param.seriesName.replace(/(.{50})/g, "$1<br>")
                : param.seriesName;

            tooltipHtml +=
              `<div style="display: flex; gap: 10px; align-items: center; margin: 10px 0">
                ` +
              `<div>` +
              param.marker +
              `</div><div>` +
              "<b>" +
              name +
              " </b> </div>" +
              `<div>` +
              param.value +
              `</div>` +
              `</div>`;
          });
          return tooltipHtml;
        },
      },
      xAxis: {
        type: "category",
        name: "Iterations",
        nameLocation: "middle",
        nameGap: 20,
        nameTextStyle: {
          fontWeight: "bold",
          fontSize: 15,
        },
        min: this.xlim[0],
        max: this.xlim[1],
        boundaryGap: false,
        data: xaxisData,
      },
      yAxis: {
        min: this.ylim[0],
        max: this.ylim[1],
        type: "value",
        name: this.data.metrics[this.tracetype],
        nameLocation: "middle",
        nameTextStyle: {
          fontWeight: "bold",
          fontSize: 15,
        },
        axisLine: {
          show: true,
        },
        axisPointer: {
          show: true,
        },
        minorTick: {
          show: true,
        },
        minorSplitLine: {
          show: true,
        },
      },
      dataZoom: [
        {
          show: true,
          type: "inside",
          filterMode: "weakFilter",
        },
      ],
      series: seriesData,
    };
  }

  areFrequencyArraysSimilar(arrays) {
    for (let i = 0; i < arrays.length; i++) {
      for (let j = i + 1; j < arrays.length; j++) {
        const [shorter, longer] =
          arrays[i].length <= arrays[j].length
            ? [arrays[i], arrays[j]]
            : [arrays[j], arrays[i]];

        // Check if `shorter` is the starting sub-array of `longer`
        if (
          !longer
            .slice(0, shorter.length)
            .every((value, index) => value === shorter[index])
        ) {
          return false; // Arrays are not similar
        }
      }
    }
    return true; // All arrays are similar
  }

  checkFrequencyDifference() {
    let frequencies = Object.entries(this.data.tracedata).map(
      ([job, job_data]) => {
        return job_data.job_data["frequency"]?.flatMap((num) =>
          Array(job_data.job_data["iters"])?.fill(num)
        );
      }
    );
    this.areFrequenciesComparable = this.areFrequencyArraysSimilar(frequencies);
  }

  /**
   * @param greyMissing grey out jobs with no data at their selected iteration (spatial view)
   */
  makeLegendData(greyMissing: boolean = true): any {
    const inactive = "#ccc";
    const legend = Object.entries(this.data.tracedata)
      .map(([job, k]) => {
        const missing = greyMissing && this.jobsWithoutData.has(job);
        return {
          name: k.job_data["job__job_name"],
          // name: k.job_info["job__job_name"].split("-")[0],
          textStyle: {
            ellipsis: true,
            overflow: "truncated",
            ...(missing && { color: inactive }),
          },
          ...(missing && { itemStyle: { color: inactive } }),
        };
      })
      .sort((a, b) => a.name?.localeCompare(b.name));
    return legend;
  }

  // ------------------------------------------------------ HEATMAP ------------------------------------

  plotHeatmap() {
    // if (this._route.snapshot.queryParams.jobs) {
    //   const jobIds = this._route.snapshot.queryParams.jobs;
    //   this.loadingChart = true;
    //   this._projectService
    //     .getTraceFitPlottingData(this.project_id, {
    //       job_ids: jobIds,
    //       parameters: [
    //         this.heatX.replace(/ /g, "_"),
    //         this.heatY.replace(/ /g, "_"),
    //       ],
    //       mode: "parameters",
    //     })
    //     .toPromise()
    //     .then((res) => {
    //       // this.data = res;
    //       this.data.results = res.results;
    //       console.log(res, this.data);
    //       this.makeHeatMap();
    //     })
    //     .catch((err) => this._message.error(err.message))
    //     .finally(() => (this.loadingChart = false));
    // }
  }

  makeHeatMap() {
    // const xData = Array.from(
    //   new Set(
    //     this.data.results.map((job) =>
    //       String(job[this.heatX.replace(/ /g, "_")])
    //     )
    //   )
    // );
    // const yData = Array.from(
    //   new Set(
    //     this.data.results.map((job) =>
    //       String(job[this.heatY.replace(/ /g, "_")])
    //     )
    //   )
    // );
    // // heatmapData = [yIndex, xIndex, mapValue]
    // let heatmapData: Array<any> = [];
    // this.data.results.forEach((job) => {
    //   let name = job.job__job_name;
    //   // the pop basically gets the last value
    //   let lastTraceValue =
    //     this.data.tracedata[name].iterations_trace[
    //       Object.keys(this.data.tracedata[name].iterations_trace).pop()
    //     ];
    //   let xpos = xData.indexOf(String(job[this.heatX.replace(/ /g, "_")]));
    //   let ypos = yData.indexOf(String(job[this.heatY.replace(/ /g, "_")]));
    //   // check if the parameter combo is a repeat
    //   let d = heatmapData.find((datum) => {
    //     let [xposPrev, yposPrev, tracePrev] = datum;
    //     if (xposPrev == xpos && yposPrev == ypos) {
    //       return datum;
    //     }
    //   });
    //   // if it is a repeat, append to array of traceValues for taking mean later
    //   if (d) {
    //     d[2].push(lastTraceValue);
    //   } else {
    //     // if not repeat, add the traceValue as an array so that if it is a repeat later, we can append
    //     heatmapData.push([xpos, ypos, [lastTraceValue]]);
    //   }
    // });
    // console.log(
    //   xData,
    //   yData,
    //   heatmapData.map((d) => [d[0], d[1], _.mean(d[2])])
    // );
    // // take the mean of the arrays of traceValues. if single, no effect, if multiple, gets avg
    // heatmapData = heatmapData.map((d) => [d[0], d[1], _.mean(d[2]).toFixed(2)]);
    // this.options = {
    //   tooltip: {
    //     show: false,
    //   },
    //   grid: {
    //     height: "50%",
    //     top: "10%",
    //   },
    //   xAxis: {
    //     type: "category",
    //     data: xData,
    //     name: this.heatX,
    //     splitArea: {
    //       show: true,
    //     },
    //   },
    //   yAxis: {
    //     type: "category",
    //     data: yData,
    //     name: this.heatY,
    //     splitArea: {
    //       show: true,
    //     },
    //   },
    //   visualMap: {
    //     min: 90,
    //     max: 100,
    //     calculable: true,
    //     orient: "horizontal",
    //     left: "center",
    //     bottom: "15%",
    //   },
    //   series: [
    //     {
    //       name: "Tracefit",
    //       type: "heatmap",
    //       data: heatmapData,
    //       label: {
    //         show: true,
    //       },
    //       emphasis: {
    //         itemStyle: {
    //           shadowBlur: 10,
    //           shadowColor: "rgba(0, 0, 0, 0.5)",
    //         },
    //       },
    //     },
    //     {
    //       name: "Tracefit2",
    //       type: "heatmap",
    //       data: heatmapData,
    //       label: {
    //         show: true,
    //       },
    //       emphasis: {
    //         itemStyle: {
    //           shadowBlur: 10,
    //           shadowColor: "rgba(0, 0, 0, 0.5)",
    //         },
    //       },
    //     },
    //   ],
    // };
  }

  // makeHeatMapOption(xKey: string, yKey: string): EChartsOption {
  //   // console.log("preemptive", this.data.results, xKey.replace(/ /g, "_"), yKey);
  //   // const xData = Array.from(
  //   //   new Set(
  //   //     this.data.results.map((job) => String(job[xKey.replace(/ /g, "_")]))
  //   //   )
  //   // );
  //   // const yData = Array.from(
  //   //   new Set(
  //   //     this.data.results.map((job) => String(job[yKey.replace(/ /g, "_")]))
  //   //   )
  //   // );

  //   // // heatmapData = [yIndex, xIndex, mapValue]
  //   // let heatmapData: Array<any> = [];
  //   // this.data.results.forEach((job) => {
  //   //   let name = job.job__job_name;
  //   //   // the pop basically gets the last value
  //   //   let lastTraceValue =
  //   //     this.data.tracedata[name].iterations_trace[
  //   //       Object.keys(this.data.tracedata[name].iterations_trace).pop()
  //   //     ];
  //   //   let xpos = xData.indexOf(String(job[xKey.replace(/ /g, "_")]));
  //   //   let ypos = yData.indexOf(String(job[yKey.replace(/ /g, "_")]));

  //   //   // check if the parameter combo is a repeat
  //   //   let d = heatmapData.find((datum) => {
  //   //     let [xposPrev, yposPrev, tracePrev] = datum;
  //   //     if (xposPrev == xpos && yposPrev == ypos) {
  //   //       return datum;
  //   //     }
  //   //   });

  //   //   // if it is a repeat, append to array of traceValues for taking mean later
  //   //   if (d) {
  //   //     d[2].push(lastTraceValue);
  //   //   } else {
  //   //     // if not repeat, add the traceValue as an array so that if it is a repeat later, we can append
  //   //     heatmapData.push([xpos, ypos, [lastTraceValue]]);
  //   //   }
  //   // });

  //   // console.log(
  //   //   xData,
  //   //   yData,
  //   //   heatmapData.map((d) => [d[0], d[1], _.mean(d[2])])
  //   // );

  //   // // take the mean of the arrays of traceValues. if single, no effect, if multiple, gets avg
  //   // heatmapData = heatmapData.map((d) => [d[0], d[1], _.mean(d[2]).toFixed(2)]);

  //   // return {
  //   //   tooltip: {
  //   //     show: false,
  //   //   },
  //   //   grid: {
  //   //     height: "50%",
  //   //     top: "10%",
  //   //   },
  //   //   xAxis: {
  //   //     type: "category",
  //   //     data: xData,
  //   //     name: xKey,
  //   //     splitArea: {
  //   //       show: true,
  //   //     },
  //   //   },
  //   //   yAxis: {
  //   //     type: "category",
  //   //     data: yData,
  //   //     name: yKey,
  //   //     splitArea: {
  //   //       show: true,
  //   //     },
  //   //   },
  //   //   visualMap: {
  //   //     min: 90,
  //   //     max: 100,
  //   //     calculable: true,
  //   //     orient: "horizontal",
  //   //     left: "center",
  //   //     bottom: "15%",
  //   //   },
  //   //   series: [
  //   //     {
  //   //       name: "Tracefit",
  //   //       type: "heatmap",
  //   //       data: heatmapData,
  //   //       label: {
  //   //         show: true,
  //   //       },
  //   //       emphasis: {
  //   //         itemStyle: {
  //   //           shadowBlur: 10,
  //   //           shadowColor: "rgba(0, 0, 0, 0.5)",
  //   //         },
  //   //       },
  //   //     },
  //   //     {
  //   //       name: "Tracefit2",
  //   //       type: "heatmap",
  //   //       data: heatmapData,
  //   //       label: {
  //   //         show: true,
  //   //       },
  //   //       emphasis: {
  //   //         itemStyle: {
  //   //           shadowBlur: 10,
  //   //           shadowColor: "rgba(0, 0, 0, 0.5)",
  //   //         },
  //   //       },
  //   //     },
  //   //   ],
  //   // };
  // }

  // plotHeatmaps() {
  //   this.options = null;

  //   let fields = Array.from(
  //     new Set(
  //       this.heatmapConfigs
  //         .map((config) => {
  //           let [x, y] = [
  //             config.x.value.replace(/ /g, "_"),
  //             config.y.value.replace(/ /g, "_"),
  //           ];
  //           return [x, y];
  //         })
  //         .flat()
  //     )
  //   );
  //   console.log("fields", fields);
  //   // get the parameters data for the selected params for those jobs
  //   if (this._route.snapshot.queryParams.jobs) {
  //     const jobIds = this._route.snapshot.queryParams.jobs;
  //     this.loadingChart = true;
  //     this._projectService
  //       .getTraceFitPlottingData(this.project_id, {
  //         job_ids: jobIds,
  //         parameters: fields,
  //         mode: "parameters",
  //       })
  //       .toPromise()
  //       .then((res) => {
  //         this.data.results = res.results;
  //         console.log(res, this.data);
  //         this.heatmapOptions = this.heatmapConfigs.map((config) => {
  //           return this.makeHeatMapOption(config.x.value, config.y.value);
  //         });
  //       })
  //       .catch((err) => this._message.error(err.message))
  //       .finally(() => (this.loadingChart = false));
  //   }
  // }

  onParametersChangedForConfig(params: { name?: string }[]) {
    this.heatmapConfigs[this.activeConfig].treeviewState = params;
    this.heatmapConfigs[this.activeConfig].x.patchValue(
      params[0]?.name || null
    );
    this.heatmapConfigs[this.activeConfig].y.patchValue(
      params[1]?.name || null
    );
  }

  swapHeatmapAxis(config: { x: string; y: string }) {
    [config.x, config.y] = [config.y, config.x];
  }
  appendHeatmapConfig() {
    this.heatmapConfigs.push({
      x: this._fb.control(null),
      y: this._fb.control(null),
      colorRange: [0, 100],
    });
  }

  // -------------------------------------------- Miscellaneous --------------------------------------------
  getJobGroups() {
    this.loadingGroups = true;
    this._projectService
      .getJobGroups(this.project_id)
      .toPromise()
      .then((res) => {
        this.jobGroups = res.data;
      })
      .catch((err) => {
        console.error(err);
        this._message.error(err.message);
      })
      .finally(() => (this.loadingGroups = false));
  }

  onGroupClick(group: GroupItem) {
    const idx = this.selectedGroups.findIndex(
      (selectedGroup) => selectedGroup.id === group.id
    );
    if (idx >= 0) {
      // remove group since already there
      this.selectedGroups.splice(idx, 1);
      // get the array of ids to remove
      let jobIds = group.jobs
        .filter((j) => !j.job__job_name.includes("QC"))
        .map((j) => j.job__id);
      // filter selectedIds and then cannt the update selection function
      this.jobsSelectedIds = this.jobsSelectedIds.filter(
        (id) => !jobIds.includes(id)
      );
      this.updateJobsSelected(this.jobsSelectedIds);
    } else {
      // add group since not already there
      this.selectedGroups.push(group);
      // push job ids to jobsSelectedIds array. Copy needed for UI update
      this.jobsSelectedIds = [
        ...this.jobsSelectedIds,
        ...group.jobs
          .filter((j) => !j.job__job_name.includes("QC"))
          .map((j) => j.job__id),
      ];
      // call the update function to update the jobs
      this.updateJobsSelected(this.jobsSelectedIds);
    }
  }

  isGroupSelected(group: GroupItem) {
    return this.selectedGroups.some(
      (selectedGroup) => selectedGroup.id === group.id
    );
  }

  plotJsonData() {
    if (!this.project_id) return;
    this.loadingChart = true;
    this._cachedProjectService.getJsonDataFromPath(this.file_path).subscribe({
      next: (data: any) => {
        try {
          this.json_metrics = data['metrics'];
          this.json_fit_type = Object.keys(this.json_metrics)[0];
          this.json_parsedData = Object.values(data['data']);
          this.json_tracedata = data['tracedata'];
          this.plotJsonScatter(false, true);
        } catch (e) {
          console.error("Error parsing JSON data:", e);
        }
        this.loadingChart = false;
      },
      error: (error) => {
        console.error("Error fetching JSON data:", error);
        this.loadingChart = false;
      }
    });
  }

  plotJsonScatter(resetXLim: boolean = false, resetYLim: boolean = false) {
    if (!this.json_parsedData.length) return;

    this.xMax = this.json_chartType === 'inline_crossline'
      ? (this.json_swapAxis ? Number(this.json_tracedata.nx2) : Number(this.json_tracedata.nx1)) * Number(this.json_tracedata.dx)
      : Number(this.json_tracedata.nx1) * Number(this.json_tracedata.dx);

    if ((this.xlim[1] && this.xlim[1] > this.xMax) || !this.xlim[1]) {
      this.xlim = [0, this.xMax];
    }

    this.json_xField = this.json_chartType === 'inline_crossline' ? (this.json_swapAxis ? 'crossline' : 'inline') : 'inline';
    this.json_yField = this.json_chartType === 'inline_crossline' ? this.json_fit_type : 'crossline';

    const yValues = this.json_parsedData.map(d => Number(d[this.json_yField])).filter(v => !isNaN(v));
    this.yMin = 0;
    this.yMax = this.json_chartType === 'inline_crossline' && yValues.length
      ? Math.max(...yValues)
      : Number(this.json_tracedata.nx2) * Number(this.json_tracedata.dx);

    if (resetYLim) {
      this.ylim = [this.yMin, this.yMax];
    } else {
      this.ylim = _.cloneDeep(this.ylim);
    }

    if (resetXLim) {
      this.xlim = [this.xMin, this.xMax];
    } else {
      this.xlim = _.cloneDeep(this.xlim);
    }

    if (this.json_chartType === 'top_down') {
      this.json_fitValues = this.json_parsedData.map(d => Number(d[this.json_fit_type])).filter(v => !isNaN(v));
      if (resetYLim) {
        this.json_minFit = Math.min(...this.json_fitValues);
        this.json_maxFit = Math.max(...this.json_fitValues);
      }
      this.json_fitRange = this.json_maxFit - this.json_minFit || 1;
    }

    const seriesData: any[] = this.json_chartType === 'top_down'
      ? this.json_parsedData.map(d => {
          const fitVal = Number(d[this.json_fit_type]);
          const norm = isNaN(fitVal) ? 0 : Math.max(0, Math.min(1, (fitVal - this.json_minFit) / this.json_fitRange));
          const g = Math.round((1 - norm) * 255);
          return {
            value: [Number(d.inline), Number(d.crossline), fitVal],
            name: String(d.shot_id),
            itemStyle: { color: `rgb(${g},${g},${g})`, borderColor: '#000000', borderWidth: 1 },
          };
        })
      : this.json_parsedData.map(d => ({
          value: [Number(d[this.json_xField]), Number(d[this.json_yField])],
          name: String(d.shot_id),
        }));

    if (this.json_plotType === 'line') {
      seriesData.sort((a, b) => (a.value[0] as number) - (b.value[0] as number));
    }

    this.options = {
      animation: false,
      grid: { left: '3%', right: '3%', bottom: '3%', containLabel: true },
      toolbox: { feature: { saveAsImage: { name: 'json-plot' }, restore: {} } },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `Shot ID: ${params.name}<br>${this.json_xField}: ${params.value[0]}<br>${this.json_yField}: ${params.value[1]}` +
          (this.json_chartType === 'top_down' ? `<br>${this.json_fit_type}: ${params.value[2]}` : ''),
      },
      xAxis: {
        type: 'value',
        name: this.json_chartType === 'top_down' ? 'inline' : this.json_xField,
        nameLocation: 'middle',
        min: 0,
        max: this.xlim[1],
        nameGap: 25,
        nameTextStyle: { fontWeight: 'bold', fontSize: 13 },
        scale: true,
      },
      yAxis: {
        type: 'value',
        name: this.json_chartType === 'top_down' ? 'crossline' : this.json_yField,
        nameLocation: 'middle',
        min: 0,
        max: this.ylim[1],
        nameGap: 50,
        nameTextStyle: { fontWeight: 'bold', fontSize: 13 },
        scale: true,
        axisLabel: { formatter: (v: number) => (this.json_chartType === 'inline_crossline' ? this.toSci(v) : String(v)) },
      },
      series: [{
        type: this.json_plotType,
        data: seriesData,
        symbolSize: 6,
        showSymbol: this.json_plotType === 'scatter',
        label: {
          show: false,
          formatter: (params: any) => params.name,
          position: 'top',
          fontSize: 10,
        },
        emphasis: {
          label: { show: true, formatter: (params: any) => params.name, fontSize: 11, fontWeight: 'bold' },
        },
      }],
      ...(this.json_chartType === 'top_down' ? {
        visualMap: {
          type: 'continuous',
          min: this.json_minFit,
          max: this.json_maxFit,
          dimension: 2,
          seriesIndex: 0,
          inRange: { color: ['#ffffff', '#000000'] },
          show: true,
          right: 10,
          top: 'center',
          text: [this.json_maxFit.toFixed(2), this.json_minFit.toFixed(2)],
          textStyle: { fontSize: 11 },
        } as any
      } : {}),
    };

    this.chartInstance?.setOption(_.cloneDeep(this.options), true);
    this._changeDetectorRef.detectChanges();
  }

  onJsonYlimChanged(e) {
    this.chartInstance.setOption({ yAxis: { min: e[0], max: e[1], type: 'value' } });
  }

  onJsonXlimChanged(e) {
    this.chartInstance.setOption({ xAxis: { min: e[0], max: e[1], type: 'value' } });
  }

  swapJsonAxis() {
    this.json_swapAxis = !this.json_swapAxis;
    this.xlim = [0, 0];
    this.plotJsonScatter(true, false);
  }

  toSci(value: number): string {
    if (value === 0) return '0';
    if (value == null || isNaN(value)) return '';
    return value.toExponential(2);
  }

  sliderStep(min: number, max: number): number {
    const range = max - min;
    if (range <= 0) return 1;
    return Math.pow(10, Math.floor(Math.log10(range / 100)));
  }

  objectKeys = Object.keys;
}
